import ImageQualityStatus from '../enums/ImageQualityStatus.js';
import VoxelDataSources from '../enums/VoxelDataSources.js';
import VoxelReductions from '../enums/VoxelReductions.js';
import VoxelStatistics from '../enums/VoxelStatistics.js';
import VoxelManager from './VoxelManager.js';
import { boundsOfFrame, boundsOfGrid, containsBounds, deriveBoxAverageGrid, gridCoversRegion, imageQualityStatusOfRecord, intersectBounds, isDefaultSelectionStatistic, mapBoundsBetweenGrids, mapIndexToNearestVoxel, reduceByBoxStatistic, requireVoxelStatistic, volumeOfBounds, voxelGridKey, voxelGridsEqual, } from './voxelGrid/index.js';
const MAX_COVERAGE_CELLS = 1 << 20;
const MAX_COVERAGE_WORK = 1 << 23;
const DERIVATION_BATCH_SLICES = 16;
function scheduleIdle(callback) {
    const requestIdle = globalThis.requestIdleCallback;
    if (requestIdle) {
        requestIdle(callback);
    }
    else {
        setTimeout(callback, 0);
    }
}
function edgesOfAxis(boxes, region, axis) {
    const edges = new Set([region[axis][0], region[axis][1] + 1]);
    for (const box of boxes) {
        edges.add(box[axis][0]);
        edges.add(box[axis][1] + 1);
    }
    return [...edges].sort((a, b) => a - b);
}
function coverageOfRegion(deliveries, region) {
    const coverage = {
        covered: 0,
        lowest: undefined,
        highest: undefined,
        deliveries: 0,
        exact: true,
    };
    const clipped = [];
    for (const delivery of deliveries ?? []) {
        const bounds = intersectBounds(delivery.bounds, region);
        if (volumeOfBounds(bounds) > 0) {
            clipped.push({ bounds, quality: delivery.quality });
            coverage.deliveries++;
        }
    }
    if (clipped.length === 0) {
        return coverage;
    }
    const boxes = clipped.map((delivery) => delivery.bounds);
    const edges = [0, 1, 2].map((axis) => edgesOfAxis(boxes, region, axis));
    const counts = edges.map((axisEdges) => axisEdges.length - 1);
    const indexOfEdge = edges.map((axisEdges) => new Map(axisEdges.map((edge, index) => [edge, index])));
    const cellCount = counts[0] * counts[1] * counts[2];
    const spans = clipped.map((delivery) => ({
        from: [0, 1, 2].map((axis) => indexOfEdge[axis].get(delivery.bounds[axis][0])),
        to: [0, 1, 2].map((axis) => indexOfEdge[axis].get(delivery.bounds[axis][1] + 1)),
    }));
    const work = spans.reduce((total, { from, to }) => total + (to[0] - from[0]) * (to[1] - from[1]) * (to[2] - from[2]), 0);
    if (cellCount > MAX_COVERAGE_CELLS || work > MAX_COVERAGE_WORK) {
        for (const delivery of clipped) {
            const volume = volumeOfBounds(delivery.bounds);
            coverage.covered = Math.max(coverage.covered, volume);
            coverage.lowest = Math.min(coverage.lowest ?? delivery.quality, delivery.quality);
            coverage.highest = Math.max(coverage.highest ?? delivery.quality, delivery.quality);
        }
        coverage.exact = false;
        return coverage;
    }
    const cells = new Array(cellCount).fill(0);
    for (let index = 0; index < clipped.length; index++) {
        const delivery = clipped[index];
        const { from, to } = spans[index];
        for (let k = from[2]; k < to[2]; k++) {
            for (let j = from[1]; j < to[1]; j++) {
                for (let i = from[0]; i < to[0]; i++) {
                    const cell = i + j * counts[0] + k * counts[0] * counts[1];
                    if (delivery.quality > cells[cell]) {
                        cells[cell] = delivery.quality;
                    }
                }
            }
        }
    }
    for (let k = 0; k < counts[2]; k++) {
        for (let j = 0; j < counts[1]; j++) {
            for (let i = 0; i < counts[0]; i++) {
                const quality = cells[i + j * counts[0] + k * counts[0] * counts[1]];
                if (quality === 0) {
                    continue;
                }
                coverage.covered +=
                    (edges[0][i + 1] - edges[0][i]) *
                        (edges[1][j + 1] - edges[1][j]) *
                        (edges[2][k + 1] - edges[2][k]);
                coverage.lowest = Math.min(coverage.lowest ?? quality, quality);
                coverage.highest = Math.max(coverage.highest ?? quality, quality);
            }
        }
    }
    return coverage;
}
function withStatus(record) {
    record.status = imageQualityStatusOfRecord(record);
    return record;
}
function voxelVolume(grid) {
    return grid.spacing[0] * grid.spacing[1] * grid.spacing[2];
}
function withinCeiling(grid, ceiling) {
    const tolerance = 1e-6;
    for (let axis = 0; axis < 3; axis++) {
        if (grid.spacing[axis] < ceiling[axis] - tolerance) {
            return false;
        }
    }
    return true;
}
function boundsKey(bounds) {
    return `${bounds[0][0]},${bounds[0][1]},${bounds[1][0]},${bounds[1][1]},${bounds[2][0]},${bounds[2][1]}`;
}
function addToDeliveryIndex(index, delivery) {
    index.byBounds.set(boundsKey(delivery.bounds), delivery);
    for (let k = delivery.bounds[2][0]; k <= delivery.bounds[2][1]; k++) {
        let bucket = index.bySlice.get(k);
        if (!bucket) {
            bucket = new Set();
            index.bySlice.set(k, bucket);
        }
        bucket.add(delivery);
    }
}
function removeFromDeliveryIndex(index, delivery) {
    index.byBounds.delete(boundsKey(delivery.bounds));
    for (let k = delivery.bounds[2][0]; k <= delivery.bounds[2][1]; k++) {
        index.bySlice.get(k)?.delete(delivery);
    }
}
function buildDeliveryIndex(delivered) {
    const index = {
        byBounds: new Map(),
        bySlice: new Map(),
    };
    for (const delivery of delivered) {
        addToDeliveryIndex(index, delivery);
    }
    return index;
}
function boxReductionOf(statistic) {
    return (requireVoxelStatistic(statistic).boxReduction ?? VoxelReductions.BoxAverage);
}
export default class CompositeVoxelManager {
    constructor({ primary, grid, statistic = VoxelStatistics.Average, quality, delivered, id, createStorage, onDerivedRegionChanged, }) {
        this.representations = [];
        this.refreshedQuality = new Map();
        this.deliveryIndexes = new WeakMap();
        this.disposed = false;
        this.getAtIJK = (i, j, k) => {
            const value = this.primary.getAtIJK(i, j, k);
            if (value !== undefined && value !== null) {
                return value;
            }
            if (this.representations.length === 1) {
                return value;
            }
            return this.valueFromSources(this.coveringRepresentations().filter((representation) => representation !== this.primaryRepresentation), this.grid, [i, j, k]);
        };
        this.getAtIJKPoint = (point) => this.getAtIJK(point[0], point[1], point[2]);
        this.getAtIndex = (index) => this.getAtIJKPoint(this.toIJK(index));
        this.setAtIJK = (i, j, k, v) => this.primary.setAtIJK(i, j, k, v);
        this.setAtIJKPoint = (point, v) => this.primary.setAtIJKPoint(point, v);
        this.setAtIndex = (index, v) => this.primary.setAtIndex(index, v);
        this.forEach = (callback, options) => this.primary.forEach(callback, options);
        this.getSliceData = (args) => this.primary.getSliceData(args);
        this.getMiddleSliceData = () => this.primary.getMiddleSliceData();
        this.primary = primary;
        this.createStorage = createStorage;
        this.onDerivedRegionChanged = onDerivedRegionChanged;
        this.compositeId = id || `composite-${primary.id}`;
        this.primaryRepresentation = {
            grid,
            statistic,
            voxelManager: primary,
            quality,
            delivered,
        };
        this.representations.push(this.primaryRepresentation);
    }
    get grid() {
        return this.primaryRepresentation.grid;
    }
    getRepresentations() {
        return [...this.representations];
    }
    addRepresentation(representation) {
        const key = voxelGridKey(representation.grid, representation.statistic);
        const existing = this.representations.findIndex((candidate) => voxelGridKey(candidate.grid, candidate.statistic) === key);
        if (existing >= 0) {
            this.representations[existing] = representation;
        }
        else {
            this.representations.push(representation);
        }
        return representation;
    }
    getRepresentation(grid, statistic = VoxelStatistics.Average) {
        const key = voxelGridKey(grid, statistic);
        return this.representations.find((candidate) => voxelGridKey(candidate.grid, candidate.statistic) === key);
    }
    selectRepresentation({ region, statistic, ceiling, } = {}) {
        const candidates = this.candidatesFor(region, statistic);
        if (candidates.length === 0) {
            return undefined;
        }
        const atOrBelowCeiling = ceiling
            ? candidates.filter((candidate) => withinCeiling(candidate.grid, ceiling))
            : candidates;
        if (atOrBelowCeiling.length > 0) {
            return this.finest(atOrBelowCeiling, region);
        }
        return this.coarsest(candidates, region);
    }
    coveringRepresentations(region, statistic) {
        return this.candidatesFor(region, statistic).sort((a, b) => voxelVolume(a.grid) - voxelVolume(b.grid));
    }
    createRepresentation({ factors, sourceGrid, sourceOffset, sourceDimensions, statistic = VoxelStatistics.Average, round = true, reduction: kind, source = VoxelDataSources.ClientDerived, }) {
        const sourceRepresentation = sourceGrid
            ? this.sourceOfDerivation(sourceGrid, { statistic })
            : this.selectRepresentation({});
        if (!sourceRepresentation) {
            throw new Error('createRepresentation: the composite holds no source for the derivation');
        }
        const reduction = {
            factors,
            sourceOffset,
            sourceDimensions,
        };
        const grid = deriveBoxAverageGrid(sourceRepresentation.grid, reduction);
        const { voxelManager, imageIds } = this.createStorageForGrid(grid, sourceRepresentation, reduction, statistic);
        const reducesAnAxis = factors.some((factor) => factor > 1);
        const boxReduction = boxReductionOf(statistic);
        const representation = this.addRepresentation({
            grid,
            statistic,
            voxelManager,
            imageIds,
            quality: sourceRepresentation.quality,
            reduction: kind ?? (reducesAnAxis ? boxReduction : VoxelReductions.None),
            source,
            derivedFrom: {
                grid: sourceRepresentation.grid,
                delivered: sourceRepresentation.delivered
                    ? [...sourceRepresentation.delivered]
                    : undefined,
                quality: sourceRepresentation.quality,
                reduction,
                round,
            },
        });
        const regions = sourceRepresentation.delivered
            ? sourceRepresentation.delivered.map((delivery) => delivery.bounds)
            : [boundsOfGrid(sourceRepresentation.grid)];
        this.deriveInBatches(representation, sourceRepresentation, regions);
        return representation;
    }
    dispose() {
        this.disposed = true;
    }
    getGridQuality(grid, { statistic = VoxelStatistics.Average, region, } = {}) {
        const existing = this.getRepresentation(grid, statistic);
        if (existing) {
            return this.getRegionQuality(existing, region);
        }
        const source = this.sourceOfDerivation(this.grid, { statistic });
        if (!source) {
            return undefined;
        }
        const reducesAnAxis = !voxelGridsEqual(grid, source.grid);
        return this.getRegionQuality({
            grid,
            statistic,
            voxelManager: undefined,
            reduction: reducesAnAxis
                ? boxReductionOf(statistic)
                : VoxelReductions.None,
            source: VoxelDataSources.ClientDerived,
            derivedFrom: {
                grid: source.grid,
                delivered: source.delivered,
                quality: source.quality,
            },
        }, region);
    }
    deriveInBatches(representation, source, regions) {
        const { derivedFrom } = representation;
        const factorK = derivedFrom.reduction.factors[2];
        const offsetK = derivedFrom.reduction.sourceOffset?.[2] ?? 0;
        const depth = representation.grid.dimensions[2];
        const batches = Math.ceil(depth / DERIVATION_BATCH_SLICES);
        const reduceBatch = (batch) => {
            const firstK = offsetK + batch * DERIVATION_BATCH_SLICES * factorK;
            const lastK = offsetK +
                Math.min((batch + 1) * DERIVATION_BATCH_SLICES, depth) * factorK -
                1;
            const sourceBounds = boundsOfGrid(source.grid);
            const slab = [
                sourceBounds[0],
                sourceBounds[1],
                [firstK, Math.min(lastK, sourceBounds[2][1])],
            ];
            for (const region of regions) {
                const part = intersectBounds(region, slab);
                if (volumeOfBounds(part) > 0) {
                    this.reduceRegionInto(representation, source, { bounds: part });
                }
            }
            derivedFrom.reducedUntil =
                batch + 1 < batches ? slab[2][1] + 1 : undefined;
            this.syncDerivedRecord(representation, source);
            return slab;
        };
        reduceBatch(0);
        if (batches <= 1) {
            representation.derivation = Promise.resolve();
            return;
        }
        representation.derivation = new Promise((resolve) => {
            const runBatch = (batch) => {
                if (this.disposed ||
                    !this.representations.includes(representation) ||
                    batch >= batches) {
                    resolve();
                    return;
                }
                const slab = reduceBatch(batch);
                this.onDerivedRegionChanged?.(intersectBounds(mapBoundsBetweenGrids(source.grid, this.grid, slab), boundsOfGrid(this.grid)));
                scheduleIdle(() => runBatch(batch + 1));
            };
            scheduleIdle(() => runBatch(1));
        });
    }
    syncDerivedRecord(representation, source) {
        const { derivedFrom } = representation;
        const { reducedUntil } = derivedFrom;
        if (reducedUntil === undefined) {
            derivedFrom.delivered = source.delivered
                ? [...source.delivered]
                : undefined;
            derivedFrom.quality = source.quality;
            return;
        }
        const sourceBounds = boundsOfGrid(source.grid);
        const reduced = [
            sourceBounds[0],
            sourceBounds[1],
            [sourceBounds[2][0], reducedUntil - 1],
        ];
        const deliveries = source.delivered ??
            (source.quality === undefined
                ? []
                : [{ bounds: sourceBounds, quality: source.quality }]);
        derivedFrom.delivered = deliveries
            .map((delivery) => ({
            bounds: intersectBounds(delivery.bounds, reduced),
            quality: delivery.quality,
        }))
            .filter((delivery) => volumeOfBounds(delivery.bounds) > 0);
        derivedFrom.quality = undefined;
    }
    acceptData({ grid, voxelManager, imageIds, statistic = VoxelStatistics.Average, quality = ImageQualityStatus.FULL_RESOLUTION, bounds, frameIndex, reduction, source, }) {
        const existing = this.getRepresentation(grid, statistic);
        const production = {
            reduction: reduction ?? existing?.reduction ?? VoxelReductions.None,
            source: source ?? existing?.source ?? VoxelDataSources.DirectLoad,
        };
        const deliveredBounds = bounds ??
            (frameIndex === undefined ? undefined : boundsOfFrame(grid, frameIndex));
        if (!deliveredBounds) {
            return this.addRepresentation({
                grid,
                statistic,
                voxelManager: voxelManager ?? existing?.voxelManager,
                imageIds: imageIds ?? existing?.imageIds,
                quality,
                delivered: existing?.delivered,
                ...production,
            });
        }
        const representation = existing ??
            this.addRepresentation({
                grid,
                statistic,
                voxelManager,
                imageIds,
                delivered: [],
                ...production,
            });
        representation.reduction = production.reduction;
        representation.source = production.source;
        if (voxelManager && representation.voxelManager !== voxelManager) {
            representation.voxelManager = voxelManager;
        }
        if (imageIds) {
            representation.imageIds = imageIds;
        }
        this.setDeliveredQuality(representation, deliveredBounds, quality);
        const refreshKey = `${voxelGridKey(grid, statistic)}#${deliveredBounds.join(',')}`;
        const refreshed = this.refreshedQuality.get(refreshKey);
        if (refreshed === undefined || refreshed < quality) {
            if (this.refreshDerivedFrames(deliveredBounds, grid, statistic) > 0) {
                this.refreshedQuality.set(refreshKey, quality);
            }
        }
        return representation;
    }
    refreshDerivedFrames(bounds, grid, statistic = VoxelStatistics.Average) {
        const delivered = this.getRepresentation(grid, statistic);
        let refreshed = 0;
        if (!delivered) {
            return 0;
        }
        for (const representation of this.representations) {
            if (!representation.derivedFrom?.reduction) {
                continue;
            }
            if (this.refreshDerivedRegion(representation, delivered, bounds)) {
                refreshed++;
            }
        }
        return refreshed;
    }
    sourceOfDerivation(grid, { statistic }) {
        return (this.getRepresentation(grid, statistic) ||
            this.getRepresentation(grid, this.primaryRepresentation.statistic));
    }
    refreshDerivedRegion(representation, delivered, bounds) {
        const { derivedFrom } = representation;
        const source = this.sourceOfDerivation(derivedFrom.grid, representation);
        if (source !== delivered) {
            return false;
        }
        if (this.reduceRegionInto(representation, source, { bounds }) === 0) {
            return false;
        }
        this.syncDerivedRecord(representation, source);
        return true;
    }
    reduceRegionInto(representation, source, { bounds }) {
        const { reduction, round = true } = representation.derivedFrom ?? {};
        if (!reduction || !source.voxelManager || !representation.voxelManager) {
            return 0;
        }
        const { factors } = reduction;
        const offset = reduction.sourceOffset ?? [0, 0, 0];
        const extent = reduction.sourceDimensions ?? source.grid.dimensions;
        const sourceOffset = [0, 0, 0];
        const sourceDimensions = [0, 0, 0];
        const targetOffset = [0, 0, 0];
        for (let axis = 0; axis < 3; axis++) {
            const first = Math.max(bounds[axis][0] - offset[axis], 0);
            const last = Math.min(bounds[axis][1] - offset[axis], extent[axis] - 1);
            if (first > last) {
                return 0;
            }
            const firstBox = Math.floor(first / factors[axis]);
            const lastBox = Math.floor(last / factors[axis]);
            sourceOffset[axis] = offset[axis] + firstBox * factors[axis];
            sourceDimensions[axis] = Math.min((lastBox - firstBox + 1) * factors[axis], offset[axis] + extent[axis] - sourceOffset[axis]);
            targetOffset[axis] = firstBox;
        }
        return reduceByBoxStatistic(source.voxelManager, { factors, sourceOffset, sourceDimensions, targetOffset }, representation.voxelManager, { statistic: representation.statistic, round });
    }
    setDeliveredQuality(representation, bounds, quality) {
        if (!representation.delivered) {
            representation.delivered = [];
        }
        const { delivered } = representation;
        const index = this.deliveryIndexOf(delivered);
        const existing = index.byBounds.get(boundsKey(bounds));
        if (existing) {
            if (existing.quality >= quality) {
                return false;
            }
            existing.quality = quality;
            return true;
        }
        const containers = index.bySlice.get(bounds[2][0]);
        if (containers) {
            for (const delivery of containers) {
                if (delivery.quality >= quality &&
                    containsBounds(delivery.bounds, bounds)) {
                    return false;
                }
            }
        }
        const contained = new Set();
        for (let k = bounds[2][0]; k <= bounds[2][1]; k++) {
            const bucket = index.bySlice.get(k);
            if (!bucket) {
                continue;
            }
            for (const delivery of bucket) {
                if (delivery.quality <= quality &&
                    containsBounds(bounds, delivery.bounds)) {
                    contained.add(delivery);
                }
            }
        }
        if (contained.size) {
            for (const delivery of contained) {
                removeFromDeliveryIndex(index, delivery);
            }
            let write = 0;
            for (let read = 0; read < delivered.length; read++) {
                if (!contained.has(delivered[read])) {
                    delivered[write++] = delivered[read];
                }
            }
            delivered.length = write;
        }
        const delivery = { bounds, quality };
        delivered.push(delivery);
        addToDeliveryIndex(index, delivery);
        return true;
    }
    deliveryIndexOf(delivered) {
        let index = this.deliveryIndexes.get(delivered);
        if (!index) {
            index = buildDeliveryIndex(delivered);
            this.deliveryIndexes.set(delivered, index);
        }
        return index;
    }
    getRegionQuality(representation, region) {
        const { grid } = representation;
        const target = region
            ? intersectBounds(mapBoundsBetweenGrids(this.grid, grid, region), boundsOfGrid(grid))
            : boundsOfGrid(grid);
        const voxels = volumeOfBounds(target);
        const record = {
            grid,
            reduction: representation.reduction ?? VoxelReductions.None,
            source: representation.source ?? VoxelDataSources.DirectLoad,
            lowest: undefined,
            highest: undefined,
            voxels,
            missing: voxels,
            deliveries: 0,
            exact: true,
            status: ImageQualityStatus.FAR_REPLICATE,
        };
        if (voxels === 0) {
            record.missing = 0;
            return withStatus(record);
        }
        if (representation.derivedFrom) {
            return withStatus(this.qualityOfDerived(representation, target, record));
        }
        const coverage = coverageOfRegion(representation.delivered, target);
        let { covered } = coverage;
        record.deliveries = coverage.deliveries;
        record.lowest = coverage.lowest;
        record.highest = coverage.highest;
        record.exact = coverage.exact;
        if (covered < voxels && representation.quality !== undefined) {
            record.lowest = Math.min(record.lowest ?? representation.quality, representation.quality);
            record.highest = Math.max(record.highest ?? representation.quality, representation.quality);
            covered = voxels;
        }
        record.missing = voxels - covered;
        return withStatus(record);
    }
    fillGrid(grid, target, statistic = VoxelStatistics.Average) {
        const sources = this.coveringRepresentations(undefined, statistic);
        if (!sources.includes(this.primaryRepresentation)) {
            sources.unshift(this.primaryRepresentation);
        }
        let written = 0;
        for (let k = 0; k < grid.dimensions[2]; k++) {
            for (let j = 0; j < grid.dimensions[1]; j++) {
                for (let i = 0; i < grid.dimensions[0]; i++) {
                    const value = this.valueFromSources(sources, grid, [i, j, k]);
                    if (value === undefined) {
                        continue;
                    }
                    target.setAtIJK(i, j, k, value);
                    written++;
                }
            }
        }
        return written;
    }
    getQuality(selector = {}) {
        const selected = this.selectRepresentation(selector);
        return selected
            ? this.getRegionQuality(selected, selector.region)
            : undefined;
    }
    qualityOfDerived(representation, target, record) {
        const { grid, delivered, quality } = representation.derivedFrom;
        const sourceBounds = intersectBounds(mapBoundsBetweenGrids(representation.grid, grid, target), boundsOfGrid(grid));
        const sourceVoxels = volumeOfBounds(sourceBounds);
        const coverage = coverageOfRegion(delivered, sourceBounds);
        record.deliveries = coverage.deliveries;
        record.lowest = coverage.lowest;
        record.highest = coverage.highest;
        record.exact = coverage.exact;
        if (sourceVoxels === 0) {
            record.missing = record.voxels;
            return withStatus(record);
        }
        let missingSource = sourceVoxels - coverage.covered;
        if (missingSource > 0 && quality !== undefined) {
            record.lowest = Math.min(record.lowest ?? quality, quality);
            record.highest = Math.max(record.highest ?? quality, quality);
            missingSource = 0;
        }
        record.missing = Math.ceil((record.voxels * missingSource) / sourceVoxels);
        record.exact =
            record.exact && (missingSource === 0 || missingSource === sourceVoxels);
        return withStatus(record);
    }
    candidatesFor(region, statistic) {
        return this.representations.filter((candidate) => {
            if (statistic) {
                if (candidate.statistic !== statistic) {
                    return false;
                }
            }
            else if (!isDefaultSelectionStatistic(candidate.statistic)) {
                return false;
            }
            if (!region) {
                return true;
            }
            return gridCoversRegion(this.grid, region, candidate.grid);
        });
    }
    finest(representations, region) {
        return representations.reduce((best, candidate) => this.better(candidate, best, -1, region) ? candidate : best);
    }
    coarsest(representations, region) {
        return representations.reduce((best, candidate) => this.better(candidate, best, 1, region) ? candidate : best);
    }
    better(candidate, best, direction, region) {
        const difference = voxelVolume(candidate.grid) - voxelVolume(best.grid);
        if (Math.abs(difference) > 1e-9) {
            return Math.sign(difference) === direction;
        }
        const candidateQuality = this.getRegionQuality(candidate, region);
        const bestQuality = this.getRegionQuality(best, region);
        if (candidateQuality.missing !== bestQuality.missing) {
            return candidateQuality.missing < bestQuality.missing;
        }
        return (candidateQuality.lowest ?? 0) > (bestQuality.lowest ?? 0);
    }
    createStorageForGrid(grid, source, reduction, statistic) {
        const stored = this.createStorage?.({ grid, statistic, reduction, source });
        if (stored?.voxelManager) {
            return stored;
        }
        const { numberOfComponents } = source.voxelManager;
        const Constructor = source.voxelManager.getConstructor();
        const length = grid.dimensions[0] *
            grid.dimensions[1] *
            grid.dimensions[2] *
            (numberOfComponents || 1);
        return {
            voxelManager: VoxelManager.createScalarVolumeVoxelManager({
                dimensions: grid.dimensions,
                scalarData: new Constructor(length),
                numberOfComponents,
                id: `${this.compositeId}-${voxelGridKey(grid, statistic)}`,
            }),
        };
    }
    valueFromSources(sources, grid, ijk) {
        for (const source of sources) {
            const sourceIJK = mapIndexToNearestVoxel(grid, source.grid, ijk);
            const { dimensions } = source.voxelManager;
            if (sourceIJK[0] < 0 ||
                sourceIJK[1] < 0 ||
                sourceIJK[2] < 0 ||
                sourceIJK[0] >= dimensions[0] ||
                sourceIJK[1] >= dimensions[1] ||
                sourceIJK[2] >= dimensions[2]) {
                continue;
            }
            const value = source.voxelManager.getAtIJK(...sourceIJK);
            if (value !== undefined && value !== null) {
                return value;
            }
        }
        return undefined;
    }
    get id() {
        return this.compositeId;
    }
    get dimensions() {
        return this.primary.dimensions;
    }
    get numberOfComponents() {
        return this.primary.numberOfComponents;
    }
    get width() {
        return this.primary.width;
    }
    set width(width) {
        this.primary.width = width;
    }
    get frameSize() {
        return this.primary.frameSize;
    }
    set frameSize(frameSize) {
        this.primary.frameSize = frameSize;
    }
    get map() {
        return this.primary.map;
    }
    set map(map) {
        this.primary.map = map;
    }
    get sourceVoxelManager() {
        return this.primary.sourceVoxelManager;
    }
    set sourceVoxelManager(voxelManager) {
        this.primary.sourceVoxelManager = voxelManager;
    }
    get points() {
        return this.primary.points;
    }
    get modifiedSlices() {
        return this.primary.modifiedSlices;
    }
    get isInObject() {
        return this.primary.isInObject;
    }
    set isInObject(isInObject) {
        this.primary.isInObject = isInObject;
    }
    get sizeInBytes() {
        return this.primary.sizeInBytes;
    }
    get bytePerVoxel() {
        return this.primary.bytePerVoxel;
    }
    get _get() {
        return this.primary._get;
    }
    get _set() {
        return this.primary._set;
    }
    get _getConstructor() {
        return this.primary._getConstructor;
    }
    get _getScalarDataLength() {
        return this.primary._getScalarDataLength;
    }
    get _getScalarData() {
        return this.primary._getScalarData;
    }
    get _updateScalarData() {
        return this.primary._updateScalarData;
    }
    get _getSliceData() {
        return this.primary._getSliceData;
    }
    get getCompleteScalarDataArray() {
        return this.primary.getCompleteScalarDataArray;
    }
    get setCompleteScalarDataArray() {
        return this.primary.setCompleteScalarDataArray;
    }
    get invalidateCache() {
        return this.primary.invalidateCache;
    }
    get getRange() {
        return this.primary.getRange;
    }
    toIJK(index) {
        return this.primary.toIJK(index);
    }
    toIndex(ijk) {
        return this.primary.toIndex(ijk);
    }
    getDefaultBounds() {
        return this.primary.getDefaultBounds();
    }
    getBoundsIJK() {
        return this.primary.getBoundsIJK();
    }
    setBounds(bounds) {
        this.primary.setBounds(bounds);
    }
    clearBounds() {
        this.primary.clearBounds();
    }
    rleForEach(callback, options) {
        this.primary.rleForEach(callback, options);
    }
    getScalarData(storeScalarData) {
        return this.primary.getScalarData(storeScalarData);
    }
    setScalarData(newScalarData) {
        this.primary.setScalarData(newScalarData);
    }
    getWritableScalarData() {
        return this.primary.getWritableScalarData();
    }
    setFromScalarData(scalarData) {
        this.primary.setFromScalarData(scalarData);
    }
    getScalarDataLength() {
        return this.primary.getScalarDataLength();
    }
    getConstructor() {
        return this.primary.getConstructor();
    }
    getArrayOfModifiedSlices() {
        return this.primary.getArrayOfModifiedSlices();
    }
    resetModifiedSlices() {
        this.primary.resetModifiedSlices();
    }
    addPoint(point) {
        this.primary.addPoint(point);
    }
    getPoints() {
        return this.primary.getPoints();
    }
    getMinMax() {
        return this.primary.getMinMax();
    }
    clear() {
        this.primary.clear();
    }
}
export { CompositeVoxelManager };
